import * as React from 'react'
import { Select as BaseSelect } from '@base-ui/react/select'

import { cn } from '@/lib/utils'

export const Select = ({
  className = '',
  variant = 'normal',
  item = '',
  items,
  label,
  placeholder = '',
  onChange,
}: {
  className?: string
  variant?: 'normal' | 'rounded'
  item?: string
  items: { label: string; value: string }[]
  label?: string
  placeholder?: string
  onChange: (value: string) => void
}) => {
  const onValueChange = (value: string | null) => {
    if (value && value !== item) onChange(value)
  }

  return (
    <div className={cn('w-full', className)}>
      <label>{label}</label>
      <BaseSelect.Root onValueChange={onValueChange} value={item} items={items}>
        <BaseSelect.Trigger
          className={cn(
            'flex h-10 w-full select-none items-center justify-between gap-3 rounded-md border border-black bg-white pl-3.5 pr-3',
            { 'h-auto rounded-full py-1 text-base': variant === 'rounded' },
          )}
        >
          <BaseSelect.Value className="data-[placeholder]:opacity-60" placeholder={placeholder} />
          <BaseSelect.Icon className="flex">
            <ChevronUpDownIcon />
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal>
          <BaseSelect.Positioner className="z-10 select-none outline-none" alignItemWithTrigger={false} sideOffset={4}>
            <BaseSelect.Popup className="group min-w-[var(--anchor-width)] origin-[var(--transform-origin)] rounded-md border bg-white bg-clip-padding text-gray-900 shadow-lg shadow-gray-200 data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)]">
              <BaseSelect.ScrollUpArrow className="top-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-white text-center text-xs before:absolute before:left-0 before:h-full before:w-full before:content-[''] data-[side=none]:before:top-[-100%]" />
              <BaseSelect.List className="relative max-h-[var(--available-height)] scroll-py-6 overflow-y-auto py-1">
                {items.map(({ label, value }) => (
                  <BaseSelect.Item
                    key={label}
                    value={value}
                    className="pointer-coarse:py-2.5 pointer-coarse:text-[0.925rem] grid cursor-default select-none grid-cols-[0.75rem_1fr] items-center gap-2 py-2 pl-2.5 pr-4 text-sm leading-4 outline-none data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-white data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-md data-[highlighted]:before:bg-black group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4"
                  >
                    <BaseSelect.ItemIndicator className="col-start-1">
                      <CheckIcon className="size-3" />
                    </BaseSelect.ItemIndicator>
                    <BaseSelect.ItemText className="col-start-2">{label}</BaseSelect.ItemText>
                  </BaseSelect.Item>
                ))}
              </BaseSelect.List>
              <BaseSelect.ScrollDownArrow className="bottom-0 z-[1] flex h-4 w-full cursor-default items-center justify-center rounded-md bg-white text-center text-xs before:absolute before:left-0 before:h-full before:w-full before:content-[''] data-[side=none]:before:bottom-[-100%]" />
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
    </div>
  )
}

function ChevronUpDownIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentcolor" strokeWidth="1.5" {...props}>
      <path d="M0.5 4.5L4 1.5L7.5 4.5" />
      <path d="M0.5 7.5L4 10.5L7.5 7.5" />
    </svg>
  )
}

function CheckIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg fill="currentcolor" width="10" height="10" viewBox="0 0 10 10" {...props}>
      <path d="M9.1603 1.12218C9.50684 1.34873 9.60427 1.81354 9.37792 2.16038L5.13603 8.66012C5.01614 8.8438 4.82192 8.96576 4.60451 8.99384C4.3871 9.02194 4.1683 8.95335 4.00574 8.80615L1.24664 6.30769C0.939709 6.02975 0.916013 5.55541 1.19372 5.24822C1.47142 4.94102 1.94536 4.91731 2.2523 5.19524L4.36085 7.10461L8.12299 1.33999C8.34934 0.993152 8.81376 0.895638 9.1603 1.12218Z" />
    </svg>
  )
}
